import { useTranslation } from 'react-i18next';
import { Linkedin, Facebook, Twitter } from 'lucide-react';
import { buildPodcastSocialShareUrls } from '@/lib/podcast-social-share';
import api from '@/lib/api';

type ShareNetwork = 'linkedin' | 'facebook' | 'twitter';

type PodcastSocialShareButtonsProps = {
  url: string;
  title: string;
  className?: string;
  /** Jeton legacy (liens `?token=`) — compteur partage réseau. */
  trackShareToken?: string;
  /** Identifiant podcast (liens `/podcasts/public/p/:id`) — compteur partage réseau. */
  trackPodcastId?: string;
};

const btnClass =
  'cursor-pointer inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_28%,#e5e7eb)] bg-[color:var(--inst-player-surface,#ffffff)] text-[color:var(--inst-player-text-muted,#6b7280)] transition-colors hover:border-[color:var(--inst-player-primary,#0C1C38)] hover:text-[color:var(--inst-player-primary,#0C1C38)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--inst-player-primary,#0C1C38)] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';

export function PodcastSocialShareButtons({
  url,
  title,
  className,
  trackShareToken,
  trackPodcastId,
}: PodcastSocialShareButtonsProps) {
  const { t } = useTranslation();
  if (!url.trim()) return null;
  const links = buildPodcastSocialShareUrls(url, title);

  /** Boutons (pas de `<a href>`) : évite toute navigation dans l’onglet courant + double ouverture avec `target="_blank"`. */
  const openShare = (href: string, network?: ShareNetwork) => {
    // Enregistrer le clic avant d’ouvrir : avec `noopener,noreferrer`, `window.open` peut renvoyer `null`
    // alors que l’onglet s’ouvre quand même — un `if (!w) return` bloquait tout le tracking.
    if (network && (trackPodcastId?.trim() || trackShareToken?.trim())) {
      void api
        .post(
          '/podcasts/public/share-network-click',
          trackPodcastId?.trim()
            ? { podcastId: trackPodcastId, network }
            : { token: trackShareToken, network },
          { skipDefaultAuth: true },
        )
        .catch(() => {
          /* compteur best-effort */
        });
    }
    void window.open(href, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={className}>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--inst-player-text-muted,#5B6472)]">
        {t('podcasts.share.socialNetworksHeading')}
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={btnClass}
          aria-label={t('podcasts.share.shareLinkedIn')}
          onClick={() => openShare(links.linkedIn, 'linkedin')}
        >
          <Linkedin className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="button"
          className={btnClass}
          aria-label={t('podcasts.share.shareFacebook')}
          onClick={() => openShare(links.facebook, 'facebook')}
        >
          <Facebook className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="button"
          className={btnClass}
          aria-label={t('podcasts.share.shareTwitter')}
          onClick={() => openShare(links.twitter, 'twitter')}
        >
          <Twitter className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
}
